import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./auth";

type Theme = "dark" | "light" | "auto";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "dark" | "light";
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>("auto");
  const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">("light");
  
  // Try to access auth context, but don't throw if it's not available
  let user;
  try {
    const auth = useAuth();
    user = auth?.user;
  } catch (error) {
    // Auth context might not be available yet, that's okay
    console.log("Auth context not available, using local storage theme only");
  }

  // Initialize theme from user preference or system
  useEffect(() => {
    // If user has a theme preference, use that
    if (user?.profile?.theme) {
      setTheme(user.profile.theme as Theme);
    }
    // Otherwise check localStorage for previously saved theme
    else {
      const savedTheme = localStorage.getItem("theme") as Theme | null;
      if (savedTheme) {
        setTheme(savedTheme);
      }
    }
  }, [user]);

  // Effect to handle theme changes and system preferences
  useEffect(() => {
    // Save theme choice to localStorage
    localStorage.setItem("theme", theme);

    // Handle auto mode by watching system preference
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    
    // Function to update the theme based on current settings
    const updateTheme = () => {
      if (theme === "dark" || (theme === "auto" && mediaQuery.matches)) {
        document.documentElement.classList.add("dark");
        setResolvedTheme("dark");
      } else {
        document.documentElement.classList.remove("dark");
        setResolvedTheme("light");
      }
    };
    
    // Set initial theme
    updateTheme();
    
    // Listen for system preference changes
    const handleChange = () => {
      if (theme === "auto") {
        updateTheme();
      }
    };
    
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

// Advanced Mode Context
interface AdvancedModeContextType {
  advancedMode: boolean;
  setAdvancedMode: (value: boolean) => void;
}

const AdvancedModeContext = createContext<AdvancedModeContextType | undefined>(undefined);

export const AdvancedModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [advancedMode, setAdvancedModeState] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('advancedMode');
      return stored === 'true';
    }
    return false;
  });

  useEffect(() => {
    localStorage.setItem('advancedMode', advancedMode ? 'true' : 'false');
  }, [advancedMode]);

  const setAdvancedMode = (value: boolean) => {
    setAdvancedModeState(value);
  };

  return (
    <AdvancedModeContext.Provider value={{ advancedMode, setAdvancedMode }}>
      {children}
    </AdvancedModeContext.Provider>
  );
};

export const useAdvancedMode = () => {
  const context = useContext(AdvancedModeContext);
  if (!context) throw new Error('useAdvancedMode must be used within AdvancedModeProvider');
  return context;
};
